import React, { useEffect, useMemo, useState } from 'react';
import { mediaStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { ratingStrings } from '../../content/ratings';
import { validateTVShowForm } from '../../validation/hobbies';
import useStepperFlow from '../../hooks/useStepperFlow';
import { includesCollectionName, normalizeCollectionOptions, resolveCollectionMode } from '../../utils/collectionOptions';
import { showFirstStepWithErrors } from '../../utils/formStepErrors';

const emptyShow = {
  id: null,
  title: '',
  overview: '',
  posterUrl: '',
  firstAirYear: '',
  genres: '',
  cast: '',
  creator: '',
  network: '',
  status: 'Returning',
  userStatus: 'Want to watch',
  favorite: false,
  rating: '',
  review: '',
  spoiler: false,
  notes: '',
  collection: 'My Shows',
  whereWatched: '',
  seasons: [],
};
const stepErrorFields = {
  details: ['title', 'overview', 'posterUrl', 'firstAirYear', 'genres', 'cast', 'creator', 'network', 'seasons'],
  library: ['status', 'userStatus', 'collection', 'whereWatched', 'favorite'],
  review: ['rating', 'review', 'spoiler'],
};

const whereWatchedOptions = [
  'Home',
  'Theater',
  'Netflix',
  'Prime Video',
  'Hulu',
  'Disney+',
  'Max',
  'Apple TV+',
  'YouTube',
  'Owned / Rental',
  'Other',
];

/**
 * Creates initial form.
 *
 * @param {*} initialData - initial data value.
 * @returns {*} The derived operation result.
 */
function createInitialForm(initialData) {
  if (!initialData) return { ...emptyShow };

  return {
    ...emptyShow,
    ...initialData,
    genres: Array.isArray(initialData.genres) ? initialData.genres.join(', ') : initialData.genres || '',
    cast: Array.isArray(initialData.cast) ? initialData.cast.join(', ') : initialData.cast || '',
    seasons: initialData.seasons || [],
  };
}

/**
 * Performs the episode label operation.
 *
 * @param {*} episode - episode value.
 * @returns {*} The derived operation result.
 */
function episodeLabel(episode) {
  return `S${episode.seasonNumber} E${episode.episodeNumber}${episode.title ? ` - ${episode.title}` : ''}`;
}

/**
 * Performs the progress for operation.
 *
 * @param {Array<*>} seasons - seasons value.
 * @returns {*} The derived operation result.
 */
function progressFor(seasons = []) {
  const episodes = seasons.flatMap((season) => season.episodes || []);
  const watched = episodes.filter((episode) => episode.watched);
  return {
    episodes,
    watched: watched.length,
    total: episodes.length,
    percent: episodes.length ? Math.round((watched.length / episodes.length) * 100) : 0,
    nextEpisode: episodes.find((episode) => !episode.watched),
  };
}

/**
 * Performs the mark all episodes watched operation.
 *
 * @param {Array<*>} seasons - seasons value.
 * @returns {*} The derived operation result.
 */
function markAllEpisodesWatched(seasons = []) {
  const today = new Date().toISOString().slice(0, 10);
  return seasons.map((season) => ({
    ...season,
    episodes: (season.episodes || []).map((episode) => ({
      ...episode,
      watched: true,
      watchedDate: episode.watchedDate || today,
    })),
  }));
}

/**
 * Performs the where watched label operation.
 *
 * @param {string} status - status value.
 * @returns {*} The derived operation result.
 */
function whereWatchedLabel(status) {
  return ['Watched', 'Abandoned'].includes(status)
    ? mediaStrings.tvshows.form.fields.watchedOn
    : mediaStrings.tvshows.form.fields.watchOn;
}

/**
 * Renders the tvstar rating experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.value - Value to inspect or transform.
 * @param {*} props.onChange - Callback invoked when change occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function TVStarRating({ value, onChange }) {
  const rating = Number(value || 0);
  const stars = Array.from({ length: 5 }, (_, index) => index + 1);

  /**
   * Retrieves star state.
   *
   * @param {*} star - star value.
   * @returns {*} The derived operation result.
   */
  const getStarState = (star) => {
    if (rating >= star) return 'full';
    if (rating >= star - 0.5) return 'half';
    return 'empty';
  };

  return (
    <div className="star-rating-wrap">
      <div className="star-rating" role="radiogroup" aria-label="TV show rating">
        {stars.map((star) => (
          <span className={`star-button ${getStarState(star)}`} key={star}>
            <span className="star-icon" aria-hidden="true">★</span>
            <button
              type="button"
              className="star-hit star-hit-left"
              onClick={() => onChange(String(star - 0.5))}
              aria-pressed={rating === star - 0.5}
              aria-label={`${star - 0.5} out of 5 stars`}
            />
            <button
              type="button"
              className="star-hit star-hit-right"
              onClick={() => onChange(String(star))}
              aria-pressed={rating === star}
              aria-label={`${star} out of 5 stars`}
            />
          </span>
        ))}
      </div>
      <div className="star-rating-summary">
        <span>{rating ? ratingStrings.value(rating) : ratingStrings.notRated}</span>
        {rating > 0 && (
          <button type="button" className="btn-ghost compact-btn" onClick={() => onChange('')}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

/** Collects TV library metadata and keeps status changes consistent with episode progress. */
export default function TVShowsForm({ theme, onSave, onCancel, isSaving, initialData, onDirtyChange, watchlists = [], isEditing = false }) {
  const initialForm = useMemo(() => createInitialForm(initialData), [initialData]);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [whereWatchedMode, setWhereWatchedMode] = useState('');
  const steps = useMemo(() => ['details', 'library', 'review'], []);
  const {
    activeSection,
    activeStepIndex,
    canGoBack,
    canGoNext,
    isStepSettled,
    goToSection,
    goToPreviousStep,
    goToNextStep,
    resetStepper,
  } = useStepperFlow(steps, 'details');
  const watchlistOptions = useMemo(
    () => normalizeCollectionOptions(watchlists),
    [watchlists]
  );
  const isKnownWatchlist = includesCollectionName(watchlistOptions, form.collection);
  const [watchlistMode, setWatchlistMode] = useState('existing');
  const progress = useMemo(() => progressFor(form.seasons), [form.seasons]);

  useEffect(() => {
    setForm(initialForm);
    resetStepper('details');
    setWhereWatchedMode(
      whereWatchedOptions.includes(initialForm.whereWatched)
        ? initialForm.whereWatched
        : initialForm.whereWatched ? 'Other' : ''
    );
  }, [initialForm, resetStepper]);

  useEffect(() => {
    if (!watchlistOptions.length) {
      setWatchlistMode('new');
      return;
    }
    setWatchlistMode(resolveCollectionMode({
      options: watchlistOptions,
      value: initialForm.collection,
      defaultValue: emptyShow.collection,
    }));
  }, [initialForm.collection, watchlistOptions]);

  useEffect(() => {
    if (watchlistMode === 'existing' && watchlistOptions.length && !includesCollectionName(watchlistOptions, form.collection)) {
      updateField('collection', watchlistOptions[0]);
    }
  }, [form.collection, watchlistMode, watchlistOptions]);

  useEffect(() => {
    onDirtyChange?.(JSON.stringify(form) !== JSON.stringify(initialForm));
  }, [form, initialForm, onDirtyChange]);

  /**
   * Updates field.
   *
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateField = (field, value) => {
    setForm((current) => {
      if (field === 'userStatus' && value === 'Watched') {
        return {
          ...current,
          userStatus: value,
          seasons: markAllEpisodesWatched(current.seasons),
        };
      }
      return { ...current, [field]: value };
    });
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  /** Updates a season or a single episode in the unsaved editor draft. */
  const setEpisodesWatched = (seasonNumber, watched, episodeId = null) => {
    const today = new Date().toISOString().slice(0, 10);
    setForm((current) => ({
      ...current,
      seasons: current.seasons.map((season) => season.seasonNumber === seasonNumber ? {
        ...season,
        episodes: (season.episodes || []).map((episode) => episodeId && episode.id !== episodeId ? episode : {
          ...episode,
          watched,
          watchedDate: watched ? episode.watchedDate || today : '',
        }),
      } : season),
    }));
  };

  /**
   * Updates where watched mode.
   *
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateWhereWatchedMode = (value) => {
    setWhereWatchedMode(value);
    updateField('whereWatched', value === 'Other' ? '' : value);
  };

  /**
   * Handles submit.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (canGoNext) {
      goToNextStep();
      return;
    }
    if (!isStepSettled) return;
    const submissionForm = form;
    if (watchlistMode === 'new' && includesCollectionName(watchlistOptions, submissionForm.collection)) {
      const nextErrors = { collection: mediaStrings.tvshows.form.errors.collectionDuplicate };
      setErrors((current) => ({ ...current, ...nextErrors }));
      showFirstStepWithErrors(nextErrors, stepErrorFields, 'library', goToSection);
      return;
    }
    const validation = validateTVShowForm(submissionForm);
    setErrors(validation.fieldErrors);
    if (!validation.valid) {
      showFirstStepWithErrors(validation.fieldErrors, stepErrorFields, 'details', goToSection);
      return;
    }
    await onSave(submissionForm);
  };

  return (
    <form className="recipe-form tv-form" onSubmit={(event) => event.preventDefault()} noValidate>
      <div className="hobby-flow-stepper" role="tablist" aria-label="TV show editor steps">
        {steps.map((section, index) => (
          <button
            key={section}
            type="button"
            className={activeSection === section ? 'active' : index < activeStepIndex ? 'complete' : ''}
            onClick={() => goToSection(section)}
            role="tab"
            aria-selected={activeSection === section}
          >
            <span>{index + 1}</span>
            {mediaStrings.tvshows.form.sections[section] || section}
          </button>
        ))}
      </div>

      {activeSection === 'details' && (
      <section className="recipe-form-section priority-section">
        <h3>{mediaStrings.tvshows.form.sections.show}</h3>
        <div className="form-grid two-columns">
          <div className="form-group">
            <label>{mediaStrings.tvshows.form.fields.title}</label>
            <input value={form.title} onChange={(event) => updateField('title', event.target.value)} aria-invalid={Boolean(errors.title)} disabled={isEditing} className={isEditing ? 'locked-field-input' : ''} />
            {errors.title && <p className="field-error">{errors.title}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.tvshows.form.fields.firstAirYear}</label>
            <input value={form.firstAirYear} onChange={(event) => updateField('firstAirYear', event.target.value)} inputMode="numeric" aria-invalid={Boolean(errors.firstAirYear)} />
            {errors.firstAirYear && <p className="field-error">{errors.firstAirYear}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.tvshows.form.fields.network}</label>
            <input value={form.network} onChange={(event) => updateField('network', event.target.value)} placeholder={mediaStrings.tvshows.form.placeholders.network} />
          </div>
          <div className="form-group">
            <label>{mediaStrings.tvshows.form.fields.creator}</label>
            <input value={form.creator} onChange={(event) => updateField('creator', event.target.value)} />
          </div>
          <div className="form-group">
            <label>{mediaStrings.tvshows.form.fields.genres}</label>
            <input value={form.genres} onChange={(event) => updateField('genres', event.target.value)} placeholder={mediaStrings.tvshows.form.placeholders.genres} />
          </div>
          <div className="form-group">
            <label>{mediaStrings.tvshows.form.fields.cast}</label>
            <input value={form.cast} onChange={(event) => updateField('cast', event.target.value)} placeholder={mediaStrings.tvshows.form.placeholders.cast} />
          </div>
          <div className="form-group">
            <label>{mediaStrings.tvshows.form.fields.status}</label>
            <select value={form.status} onChange={(event) => updateField('status', event.target.value)} aria-invalid={Boolean(errors.status)}>
              {mediaStrings.tvshows.form.options.productionStatuses.map((status) => <option key={status}>{status}</option>)}
            </select>
            {errors.status && <p className="field-error">{errors.status}</p>}
          </div>
          <div className="form-group form-grid-full">
            <label>{mediaStrings.tvshows.form.fields.overview}</label>
            <textarea rows="3" value={form.overview} onChange={(event) => updateField('overview', event.target.value)} />
          </div>
        </div>
      </section>
      )}

      {activeSection === 'library' && (
      <>
      <section className="recipe-form-section">
        <h3>{mediaStrings.tvshows.form.sections.library}</h3>
        <div className="form-grid two-columns">
          <div className="form-group">
            <label>{mediaStrings.tvshows.form.fields.userStatus}</label>
            <select value={form.userStatus} onChange={(event) => updateField('userStatus', event.target.value)} aria-invalid={Boolean(errors.userStatus)}>
              {mediaStrings.tvshows.form.options.userStatuses.map((status) => <option key={status}>{status}</option>)}
            </select>
            {errors.userStatus && <p className="field-error">{errors.userStatus}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.tvshows.form.fields.collection}</label>
            <div className="collection-picker">
              {watchlistOptions.length > 0 && (
                <div className="collection-mode-toggle" role="group" aria-label={mediaStrings.tvshows.form.options.watchlistMode}>
                  <button
                    type="button"
                    className={watchlistMode === 'existing' ? 'active' : ''}
                    onClick={() => {
                      setWatchlistMode('existing');
                      updateField('collection', isKnownWatchlist ? form.collection : watchlistOptions[0]);
                    }}
                  >
                    {mediaStrings.movies.form.options.existing}
                  </button>
                  <button
                    type="button"
                    className={watchlistMode === 'new' ? 'active' : ''}
                    onClick={() => {
                      setWatchlistMode('new');
                      if (isKnownWatchlist) updateField('collection', '');
                    }}
                  >
                    {mediaStrings.movies.form.options.createNew}
                  </button>
                </div>
              )}
              {watchlistMode === 'existing' && watchlistOptions.length > 0 ? (
                <select value={isKnownWatchlist ? form.collection : watchlistOptions[0]} onChange={(event) => updateField('collection', event.target.value)}>
                  {watchlistOptions.map((watchlist) => <option key={watchlist} value={watchlist}>{watchlist}</option>)}
                </select>
              ) : (
                <input value={form.collection} onChange={(event) => updateField('collection', event.target.value)} placeholder={mediaStrings.tvshows.form.placeholders.collection} />
              )}
            </div>
            {errors.collection && <p className="field-error">{errors.collection}</p>}
          </div>
          <div className="form-group">
            <label>{whereWatchedLabel(form.userStatus)}</label>
            <select value={whereWatchedMode} onChange={(event) => updateWhereWatchedMode(event.target.value)}>
              <option value="">{mediaStrings.movies.form.options.notSelected}</option>
              {whereWatchedOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
          {whereWatchedMode === 'Other' && (
            <div className="form-group">
              <label>{mediaStrings.tvshows.form.fields.otherLocation}</label>
              <input
                value={form.whereWatched}
                onChange={(event) => updateField('whereWatched', event.target.value)}
                placeholder={mediaStrings.tvshows.form.placeholders.otherLocation}
              />
            </div>
          )}
          <div className="form-group form-grid-full">
            <button
              type="button"
              className={`favorite-toggle inline-favorite-toggle ${form.favorite ? 'active' : ''}`}
              onClick={() => updateField('favorite', !form.favorite)}
              aria-pressed={form.favorite}
            >
              <span>{form.favorite ? mediaStrings.tvshows.form.favorite : mediaStrings.tvshows.form.favoriteAction}</span>
            </button>
          </div>
        </div>
      </section>

      <section className="recipe-form-section">
        <h3>{mediaStrings.tvshows.progressTitle}</h3>
        {progress.total > 0 ? (
          <div className="tv-season-list">
            <div className="tv-up-next">
              <span>
                {progress.watched}/{progress.total} episodes • {progress.percent}% watched
              </span>
              <strong>{progress.nextEpisode ? mediaStrings.tvshows.upNextEpisode(episodeLabel(progress.nextEpisode)) : mediaStrings.tvshows.caughtUp}</strong>
            </div>
            {form.seasons.map((season) => (
              <div className="tv-season" key={season.seasonNumber}>
                <div className="tv-season-heading">
                  <h4>{mediaStrings.tvshows.seasonTitle(season.seasonNumber)}</h4>
                  <span>{mediaStrings.tvshows.episodes((season.episodes || []).filter((episode) => episode.watched).length, (season.episodes || []).length)}</span>
                </div>
                {(season.episodes || []).length > 0 && (
                  <div className="tv-season-actions">
                    <button type="button" className="btn-ghost compact-btn" onClick={() => setEpisodesWatched(season.seasonNumber, true)}>{mediaStrings.tvshows.markAllWatched}</button>
                    <button type="button" className="btn-ghost compact-btn" onClick={() => setEpisodesWatched(season.seasonNumber, false)}>{mediaStrings.tvshows.clearSeason}</button>
                  </div>
                )}
                <div className="tv-episode-list">
                  {(season.episodes || []).map((episode) => (
                    <button type="button" className={`tv-episode-row ${episode.watched ? 'watched' : ''}`} key={episode.id} onClick={() => setEpisodesWatched(season.seasonNumber, !episode.watched, episode.id)} aria-pressed={episode.watched}>
                      <span className={`tv-episode-check ${episode.watched ? 'watched' : ''}`} aria-hidden="true">{episode.watched ? '✓' : ''}</span>
                      <span className="tv-episode-label">{episodeLabel(episode)}</span>
                      <strong>{episode.watched ? mediaStrings.tvshows.watched : mediaStrings.tvshows.unwatched}</strong>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted-copy">{mediaStrings.tvshows.episodeDetailsUnavailable}</p>
        )}
      </section>
      </>
      )}

      {activeSection === 'review' && (
      <section className="recipe-form-section">
        <h3>{mediaStrings.tvshows.form.sections.review}</h3>
        <div className="form-grid two-columns">
          <div className="form-group form-grid-full">
            <label>{mediaStrings.tvshows.form.fields.rating}</label>
            <TVStarRating value={form.rating} onChange={(value) => updateField('rating', value)} />
            {errors.rating && <p className="field-error">{errors.rating}</p>}
          </div>

          <div className="form-group form-grid-full">
            <label className="checkbox-card inline-checkbox-card">
              <input
                type="checkbox"
                checked={form.spoiler}
                onChange={(event) => updateField('spoiler', event.target.checked)}
              />
              <span>{mediaStrings.tvshows.form.options.spoiler}</span>
            </label>
          </div>

          <div className="form-group form-grid-full">
            <label>{mediaStrings.tvshows.form.fields.review}</label>
            <textarea rows="5" value={form.review} onChange={(event) => updateField('review', event.target.value)} />
          </div>
        </div>
      </section>
      )}

      <div className="form-actions sticky-actions">
        <button type="button" className="btn-ghost" onClick={() => onCancel({ confirmDiscard: true })}>{moduleCommonStrings.actions.cancel}</button>
        <div className="stepper-actions">
          {canGoBack && (
            <button type="button" className="btn-secondary" onClick={goToPreviousStep}>
              {moduleCommonStrings.actions.back}
            </button>
          )}
          {canGoNext ? (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={goToNextStep}>
              {moduleCommonStrings.actions.next}
            </button>
          ) : (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={handleSubmit} disabled={isSaving || !isStepSettled}>
              {isSaving ? moduleCommonStrings.actions.saving : mediaStrings.tvshows.form.save}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
